<template>
    <div class="page levels-page">
        <div class="page-header">
            <h1>等級設定</h1>
            <p>管理用戶等級系統設定</p>
        </div>

        <div class="content vaporwave-theme">
            <!-- 工具欄 -->
            <div class="toolbar toolbar-grid levels-toolbar">
                <div class="filters-grid">
                    <a-row :gutter="[16, 16]">
                        <a-col :xs="24" :sm="12" :md="10" :lg="7">
                            <div class="filter-field">
                                <span class="filter-field-label">等級範圍</span>
                                <div class="amount-range-wrap">
                                    <a-input-number
                                        v-model:value="filters.level_min"
                                        placeholder="最小等級"
                                        :min="1"
                                        :precision="0"
                                        class="status-select amount-input w-full"
                                    />
                                    <span class="amount-sep">-</span>
                                    <a-input-number
                                        v-model:value="filters.level_max"
                                        placeholder="最大等級"
                                        :min="1"
                                        :precision="0"
                                        class="status-select amount-input w-full"
                                    />
                                </div>
                            </div>
                        </a-col>
                        <a-col :xs="24" :sm="12" :md="7" :lg="5">
                            <div class="filter-field">
                                <span class="filter-field-label">身份</span>
                                <a-select
                                    v-model:value="filters.identities"
                                    mode="multiple"
                                    show-search
                                    option-filter-prop="label"
                                    placeholder="選擇身份"
                                    class="status-select w-full"
                                    allow-clear
                                    :options="identityFilterOptions"
                                />
                            </div>
                        </a-col>
                        <a-col :xs="24" :sm="12" :md="7" :lg="5">
                            <div class="filter-field">
                                <span class="filter-field-label">聘階</span>
                                <a-select
                                    v-model:value="filters.rank_ids"
                                    mode="multiple"
                                    show-search
                                    option-filter-prop="label"
                                    placeholder="選擇聘階"
                                    class="status-select w-full"
                                    allow-clear
                                    :options="rankFilterOptions"
                                />
                            </div>
                        </a-col>
                        <a-col :xs="24" :sm="12" :md="24" :lg="7">
                            <div class="filter-field filter-field--actions">
                                <span
                                    class="filter-field-label filter-field-label--placeholder"
                                >
                                    &nbsp;
                                </span>
                                <div class="toolbar-query-group">
                                    <a-button
                                        size="middle"
                                        class="unified-search-btn"
                                        @click="handleFilterChange"
                                    >
                                        查詢
                                    </a-button>
                                    <a-button
                                        size="middle"
                                        class="unified-reset-btn"
                                        @click="handleResetFilters"
                                    >
                                        重置
                                    </a-button>
                                </div>
                            </div>
                        </a-col>
                    </a-row>
                </div>
                <div class="toolbar-actions levels-toolbar-actions">
                    <a-space class="sort-controls" wrap :size="8">
                        <a-select
                            v-model:value="currentSort"
                            class="status-select levels-sort-select"
                            @change="handleSortChange"
                        >
                            <a-select-option value="level">
                                等級
                            </a-select-option>
                            <a-select-option value="created_at">
                                創建時間
                            </a-select-option>
                            <a-select-option value="updated_at">
                                更新時間
                            </a-select-option>
                        </a-select>
                        <a-button-group>
                            <a-button
                                size="middle"
                                :class="[
                                    'sort-order-btn',
                                    currentOrder === 'asc'
                                        ? 'sort-order-active'
                                        : ''
                                ]"
                                @click="handleOrderChange('asc')"
                            >
                                升序
                            </a-button>
                            <a-button
                                size="middle"
                                :class="[
                                    'sort-order-btn',
                                    currentOrder === 'desc'
                                        ? 'sort-order-active'
                                        : ''
                                ]"
                                @click="handleOrderChange('desc')"
                            >
                                降序
                            </a-button>
                        </a-button-group>
                    </a-space>
                    <a-space class="view-controls" wrap :size="8">
                        <a-radio-group
                            v-model:value="viewMode"
                            button-style="solid"
                            class="view-mode-switch"
                        >
                            <a-radio-button value="card">
                                <template #icon>
                                    <AppstoreOutlined />
                                </template>
                                卡片視圖
                            </a-radio-button>
                            <a-radio-button value="group">
                                <template #icon>
                                    <UnorderedListOutlined />
                                </template>
                                分組視圖
                            </a-radio-button>
                        </a-radio-group>
                        <a-button
                            v-if="canCreate"
                            type="primary"
                            class="unified-add-btn"
                            @click="showCreateModal"
                        >
                            新增等級
                        </a-button>
                    </a-space>
                </div>
            </div>

            <!-- 卡片視圖 -->
            <div v-if="viewMode === 'card'" class="levels-card-view">
                <div class="levels-grid">
                    <div
                        v-for="level in filteredLevelsList"
                        :key="level.id"
                        class="level-card"
                        :class="{ 'cursor-pointer': canView }"
                        @click="canView ? viewLevel(level) : null"
                    >
                        <!-- 操作按鈕（懸浮在右上角） -->
                        <div
                            v-if="canEdit || canDelete"
                            class="level-card-actions"
                        >
                            <a-button
                                v-if="canEdit"
                                size="small"
                                class="action-btn-edit"
                                @click.stop="editLevel(level)"
                                title="編輯"
                            >
                                <template #icon>
                                    <EditOutlined />
                                </template>
                            </a-button>
                            <a-button
                                v-if="canDelete"
                                size="small"
                                class="action-btn-delete"
                                danger
                                @click.stop="openDeleteModal(level)"
                                title="刪除"
                            >
                                <template #icon>
                                    <DeleteOutlined />
                                </template>
                            </a-button>
                        </div>
                        <div class="level-card-header">
                            <div class="level-badge-large">
                                <span class="level-text-large">
                                    Level {{ level.level }}
                                </span>
                            </div>
                        </div>
                        <div class="level-card-body">
                            <div
                                v-if="level.unlock_description"
                                class="level-description"
                            >
                                {{ level.unlock_description }}
                            </div>
                            <div class="level-tags">
                                <!-- 身份顯示中文名稱 -->
                                <a-tag
                                    v-for="(
                                        identity, idx
                                    ) in level.identity_limits"
                                    :key="idx"
                                    color="blue"
                                    size="small"
                                >
                                    {{
                                        typeof identity === 'object'
                                            ? identity.identity_name ||
                                              identity.label
                                            : getIdentityLabel(identity)
                                    }}
                                </a-tag>
                                <!-- 聘階顯示 -->
                                <a-tag
                                    v-for="(rank, idx) in level.rank_limits"
                                    :key="idx"
                                    color="purple"
                                    size="small"
                                >
                                    {{ rank.rank_name || rank.name || rank }}
                                </a-tag>
                                <!-- 條件顯示：xxCP、xxPP -->
                                <span
                                    v-for="(req, idx) in level.requirements"
                                    :key="idx"
                                    class="requirement-badge"
                                >
                                    {{
                                        req.requirement_type === 'point' &&
                                        req.point_type_code
                                            ? `${req.required_count || req.count || 0}${req.point_type_code}`
                                            : req.requirement_type === 'task'
                                              ? '任務'
                                              : req.requirement_type
                                    }}
                                </span>
                                <!-- 獎勵簡約版 -->
                                <span
                                    v-if="level.rewards?.length"
                                    class="reward-badge"
                                >
                                    獎勵
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    v-if="filteredLevelsList.length === 0"
                    class="empty-levels"
                >
                    <p>暫無等級資料</p>
                </div>
                <!-- 載入更多按鈕 -->
                <div
                    v-if="
                        filteredLevelsList.length > 0 &&
                        cardViewPagination.hasMore
                    "
                    class="load-more-wrapper"
                >
                    <a-button
                        type="primary"
                        :loading="loading"
                        @click="handleLoadMore"
                    >
                        載入更多
                    </a-button>
                </div>
            </div>

            <!-- 分組視圖（圖表形式） -->
            <div v-if="viewMode === 'group'" class="levels-group-view">
                <!-- 無限制等級（按等級排序，放在最前面） -->
                <div v-if="unrestrictedLevels.length > 0" class="group-section">
                    <div
                        class="group-header-card"
                        @click="toggleGroupExpand('unrestricted')"
                    >
                        <div class="group-title-row">
                            <h3 class="group-title">無限制等級</h3>
                            <span
                                v-if="unrestrictedLevels.length > 0"
                                class="level-range-badge"
                            >
                                {{ unrestrictedLevels[0]?.level }}-{{
                                    unrestrictedLevels[
                                        unrestrictedLevels.length - 1
                                    ]?.level
                                }}等
                            </span>
                            <div class="group-expand-icon">
                                <DownOutlined
                                    v-if="!expandedGroups.has('unrestricted')"
                                />
                                <UpOutlined v-else />
                            </div>
                        </div>
                    </div>
                    <div
                        v-show="expandedGroups.has('unrestricted')"
                        class="levels-timeline"
                    >
                        <div
                            v-for="level in unrestrictedLevels"
                            :key="level.id"
                            class="level-row-card"
                        >
                            <div
                                class="level-row-header"
                                :class="{ 'cursor-pointer': canView }"
                                @click="canView ? viewLevel(level) : null"
                            >
                                <div class="level-number">
                                    <span class="level-text">
                                        Lv.{{ level.level }}
                                    </span>
                                </div>
                                <div class="level-info">
                                    <div
                                        v-if="level.unlock_description"
                                        class="info-row description-row"
                                    >
                                        <span
                                            class="info-value description-text"
                                        >
                                            {{ level.unlock_description }}
                                        </span>
                                    </div>
                                    <div class="info-row">
                                        <span class="info-label">條件：</span>
                                        <div class="tags-container">
                                            <a-tag
                                                v-for="(
                                                    req, idx
                                                ) in level.requirements || []"
                                                :key="idx"
                                                color="orange"
                                                size="small"
                                            >
                                                {{
                                                    (req.requirement_type ||
                                                        req.type) === 'point' &&
                                                    req.point_type_code
                                                        ? `${req.required_count || req.count || 0}${req.point_type_code}`
                                                        : (req.requirement_type ||
                                                                req.type) ===
                                                            'task'
                                                          ? '任務'
                                                          : '條件'
                                                }}
                                            </a-tag>
                                            <span
                                                v-if="
                                                    !level.requirements?.length
                                                "
                                                class="no-data-text"
                                            >
                                                無條件
                                            </span>
                                        </div>
                                    </div>
                                    <div class="info-row">
                                        <span class="info-label">獎勵：</span>
                                        <div class="tags-container">
                                            <a-tag
                                                v-if="level.rewards?.length"
                                                color="green"
                                                size="small"
                                            >
                                                獎勵
                                            </a-tag>
                                            <span v-else class="no-data-text">
                                                無獎勵
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-if="canEdit || canDelete"
                                    class="level-actions"
                                >
                                    <a-button
                                        v-if="canEdit"
                                        size="small"
                                        class="action-btn-edit"
                                        @click.stop="editLevel(level)"
                                        title="編輯"
                                    >
                                        <template #icon>
                                            <EditOutlined />
                                        </template>
                                    </a-button>
                                    <a-button
                                        v-if="canDelete"
                                        size="small"
                                        class="action-btn-delete"
                                        danger
                                        @click.stop="openDeleteModal(level)"
                                        title="刪除"
                                    >
                                        <template #icon>
                                            <DeleteOutlined />
                                        </template>
                                    </a-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 身份分組 -->
                <div
                    v-for="group in identityGroups"
                    :key="`identity-${group.key}`"
                    class="group-section"
                >
                    <div
                        class="group-header-card"
                        @click="toggleGroupExpand(`identity-${group.key}`)"
                    >
                        <div class="group-title-row">
                            <h3 class="group-title">{{ group.label }}</h3>
                            <span class="level-range-badge">
                                {{ group.minLevel }}-{{ group.maxLevel }}等
                            </span>
                            <div class="group-expand-icon">
                                <DownOutlined
                                    v-if="
                                        !expandedGroups.has(
                                            `identity-${group.key}`
                                        )
                                    "
                                />
                                <UpOutlined v-else />
                            </div>
                        </div>
                    </div>
                    <div
                        v-show="expandedGroups.has(`identity-${group.key}`)"
                        class="levels-timeline"
                    >
                        <div
                            v-for="level in group.levels"
                            :key="level.id"
                            class="level-row-card"
                        >
                            <div
                                class="level-row-header"
                                :class="{ 'cursor-pointer': canView }"
                                @click="canView ? viewLevel(level) : null"
                            >
                                <div class="level-number">
                                    <span class="level-text">
                                        Lv.{{ level.level }}
                                    </span>
                                </div>
                                <div class="level-info">
                                    <div
                                        v-if="level.unlock_description"
                                        class="info-row description-row"
                                    >
                                        <span
                                            class="info-value description-text"
                                        >
                                            {{ level.unlock_description }}
                                        </span>
                                    </div>
                                    <div class="info-row">
                                        <span class="info-label">條件：</span>
                                        <div class="tags-container">
                                            <a-tag
                                                v-for="(
                                                    req, idx
                                                ) in level.requirements || []"
                                                :key="idx"
                                                color="orange"
                                                size="small"
                                            >
                                                {{
                                                    (req.requirement_type ||
                                                        req.type) === 'point' &&
                                                    req.point_type_code
                                                        ? `${req.required_count || req.count || 0}${req.point_type_code}`
                                                        : (req.requirement_type ||
                                                                req.type) ===
                                                            'task'
                                                          ? '任務'
                                                          : '條件'
                                                }}
                                            </a-tag>
                                            <span
                                                v-if="
                                                    !level.requirements?.length
                                                "
                                                class="no-data-text"
                                            >
                                                無條件
                                            </span>
                                        </div>
                                    </div>
                                    <div class="info-row">
                                        <span class="info-label">獎勵：</span>
                                        <div class="tags-container">
                                            <a-tag
                                                v-if="level.rewards?.length"
                                                color="green"
                                                size="small"
                                            >
                                                獎勵
                                            </a-tag>
                                            <span v-else class="no-data-text">
                                                無獎勵
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-if="canEdit || canDelete"
                                    class="level-actions"
                                >
                                    <a-button
                                        v-if="canEdit"
                                        size="small"
                                        class="action-btn-edit"
                                        @click.stop="editLevel(level)"
                                        title="編輯"
                                    >
                                        <template #icon>
                                            <EditOutlined />
                                        </template>
                                    </a-button>
                                    <a-button
                                        v-if="canDelete"
                                        size="small"
                                        class="action-btn-delete"
                                        danger
                                        @click.stop="openDeleteModal(level)"
                                        title="刪除"
                                    >
                                        <template #icon>
                                            <DeleteOutlined />
                                        </template>
                                    </a-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 聘階分組 -->
                <div
                    v-for="group in rankGroups"
                    :key="`rank-${group.key}`"
                    class="group-section"
                >
                    <div
                        class="group-header-card"
                        @click="toggleGroupExpand(`rank-${group.key}`)"
                    >
                        <div class="group-title-row">
                            <h3 class="group-title">{{ group.label }}</h3>
                            <span class="level-range-badge">
                                {{ group.minLevel }}-{{ group.maxLevel }}等
                            </span>
                            <div class="group-expand-icon">
                                <DownOutlined
                                    v-if="
                                        !expandedGroups.has(`rank-${group.key}`)
                                    "
                                />
                                <UpOutlined v-else />
                            </div>
                        </div>
                    </div>
                    <div
                        v-show="expandedGroups.has(`rank-${group.key}`)"
                        class="levels-timeline"
                    >
                        <div
                            v-for="level in group.levels"
                            :key="level.id"
                            class="level-row-card"
                        >
                            <div
                                class="level-row-header"
                                :class="{ 'cursor-pointer': canView }"
                                @click="canView ? viewLevel(level) : null"
                            >
                                <div class="level-number">
                                    <span class="level-text">
                                        Lv.{{ level.level }}
                                    </span>
                                </div>
                                <div class="level-info">
                                    <div
                                        v-if="level.unlock_description"
                                        class="info-row description-row"
                                    >
                                        <span
                                            class="info-value description-text"
                                        >
                                            {{ level.unlock_description }}
                                        </span>
                                    </div>
                                    <div class="info-row">
                                        <span class="info-label">條件：</span>
                                        <div class="tags-container">
                                            <a-tag
                                                v-for="(
                                                    req, idx
                                                ) in level.requirements || []"
                                                :key="idx"
                                                color="orange"
                                                size="small"
                                            >
                                                {{
                                                    (req.requirement_type ||
                                                        req.type) === 'point' &&
                                                    req.point_type_code
                                                        ? `${req.required_count || req.count || 0}${req.point_type_code}`
                                                        : (req.requirement_type ||
                                                                req.type) ===
                                                            'task'
                                                          ? '任務'
                                                          : '條件'
                                                }}
                                            </a-tag>
                                            <span
                                                v-if="
                                                    !level.requirements?.length
                                                "
                                                class="no-data-text"
                                            >
                                                無條件
                                            </span>
                                        </div>
                                    </div>
                                    <div class="info-row">
                                        <span class="info-label">獎勵：</span>
                                        <div class="tags-container">
                                            <a-tag
                                                v-if="level.rewards?.length"
                                                color="green"
                                                size="small"
                                            >
                                                獎勵
                                            </a-tag>
                                            <span v-else class="no-data-text">
                                                無獎勵
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-if="canEdit || canDelete"
                                    class="level-actions"
                                >
                                    <a-button
                                        v-if="canEdit"
                                        size="small"
                                        class="action-btn-edit"
                                        @click.stop="editLevel(level)"
                                        title="編輯"
                                    >
                                        <template #icon>
                                            <EditOutlined />
                                        </template>
                                    </a-button>
                                    <a-button
                                        v-if="canDelete"
                                        size="small"
                                        class="action-btn-delete"
                                        danger
                                        @click.stop="openDeleteModal(level)"
                                        title="刪除"
                                    >
                                        <template #icon>
                                            <DeleteOutlined />
                                        </template>
                                    </a-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 空狀態 -->
                <div
                    v-if="
                        identityGroups.length === 0 &&
                        rankGroups.length === 0 &&
                        unrestrictedLevels.length === 0
                    "
                    class="empty-state"
                >
                    <p>暫無等級資料</p>
                </div>

                <!-- 載入更多按鈕 -->
                <div
                    v-if="groupViewPagination.hasMore"
                    class="load-more-wrapper"
                >
                    <a-button
                        type="primary"
                        :loading="loading"
                        @click="handleGroupViewLoadMore"
                    >
                        載入更多
                    </a-button>
                </div>
            </div>
        </div>

        <!-- 新增/編輯等級 Modal -->
        <VaporwaveModal
            v-model:open="editModalVisible"
            :title="isEditMode ? '編輯等級設定' : '新增等級設定'"
            width="900px"
            :confirm-loading="confirmLoading"
            @ok="handleSave"
            @cancel="handleCancel"
        >
            <div class="level-config-form-container">
                <a-form
                    ref="basicFormRef"
                    :model="formData.level"
                    :rules="basicFormRules"
                    layout="vertical"
                    class="vaporwave-form"
                    autocomplete="off"
                >
                    <a-collapse
                        v-model:activeKey="sectionsActiveKeys"
                        :bordered="false"
                        class="form-sections-collapse"
                    >
                        <!-- 基本設定 -->
                        <a-collapse-panel
                            key="basic"
                            class="form-section-panel"
                        >
                            <template #header>
                                <span class="section-header">基本設定</span>
                            </template>
                            <a-card
                                class="form-section-card-inner"
                                :bordered="false"
                            >
                                <a-form-item label="等級" name="level">
                                    <a-input-number
                                        v-model:value="formData.level.level"
                                        :min="1"
                                        :max="999"
                                        placeholder="請輸入等級"
                                        class="level-input"
                                        autocomplete="off"
                                        data-form-type="other"
                                    />
                                </a-form-item>
                                <a-form-item
                                    label="解鎖說明"
                                    name="unlock_description"
                                >
                                    <a-textarea
                                        v-model:value="
                                            formData.level.unlock_description
                                        "
                                        placeholder="請輸入解鎖說明"
                                        :rows="4"
                                        :maxlength="500"
                                        show-count
                                        autocomplete="off"
                                        data-form-type="other"
                                    />
                                </a-form-item>
                            </a-card>
                        </a-collapse-panel>

                        <!-- 身份與聘階限制 -->
                        <a-collapse-panel
                            key="limits"
                            class="form-section-panel"
                        >
                            <template #header>
                                <span class="section-header">
                                    身份與聘階限制
                                </span>
                            </template>
                            <a-card
                                class="form-section-card-inner"
                                :bordered="false"
                            >
                                <div class="limits-section">
                                    <div class="section-item">
                                        <div class="section-header">
                                            <div>
                                                <p class="section-desc">
                                                    選擇可升級此等級的身份（不選擇表示無限制）
                                                </p>
                                            </div>
                                            <a-button
                                                type="primary"
                                                size="small"
                                                @click="addIdentityLimit"
                                            >
                                                新增身份限制
                                            </a-button>
                                        </div>
                                        <div
                                            v-if="
                                                formData.identities.length === 0
                                            "
                                            class="empty-state"
                                        >
                                            <p>
                                                暫無身份限制，點擊上方按鈕新增
                                            </p>
                                        </div>
                                        <div
                                            v-else
                                            class="identity-limits-list"
                                        >
                                            <div
                                                v-for="(
                                                    identity, index
                                                ) in formData.identities"
                                                :key="index"
                                                class="identity-limit-item"
                                            >
                                                <a-row
                                                    :gutter="16"
                                                    align="middle"
                                                >
                                                    <a-col :span="20">
                                                        <a-select
                                                            v-model:value="
                                                                formData
                                                                    .identities[
                                                                    index
                                                                ]
                                                            "
                                                            placeholder="選擇角色"
                                                            :options="
                                                                identityLimitOptions
                                                            "
                                                            class="w-full"
                                                        />
                                                    </a-col>
                                                    <a-col :span="4">
                                                        <a-button
                                                            type="text"
                                                            class="delete-btn"
                                                            @click="
                                                                removeIdentityLimit(
                                                                    index
                                                                )
                                                            "
                                                        >
                                                            <template #icon>
                                                                <DeleteOutlined />
                                                            </template>
                                                        </a-button>
                                                    </a-col>
                                                </a-row>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- 分割線 -->
                                    <a-divider class="section-divider" />

                                    <div class="section-item">
                                        <p class="section-desc">
                                            選擇可升級此等級的聘階（不選擇表示無限制）
                                        </p>
                                        <a-select
                                            v-model:value="formData.rank_ids"
                                            mode="multiple"
                                            show-search
                                            option-filter-prop="label"
                                            placeholder="選擇聘階限制"
                                            class="w-full"
                                            :options="rankOptions"
                                        />
                                    </div>
                                </div>
                            </a-card>
                        </a-collapse-panel>

                        <!-- 升級條件 -->
                        <a-collapse-panel
                            key="requirements"
                            class="form-section-panel"
                        >
                            <template #header>
                                <span class="section-header">升級條件</span>
                            </template>
                            <a-card
                                class="form-section-card-inner"
                                :bordered="false"
                            >
                                <div class="requirements-section">
                                    <div class="section-header">
                                        <div>
                                            <p class="section-desc">
                                                設定達到此等級所需的條件
                                            </p>
                                        </div>
                                        <a-button
                                            type="primary"
                                            size="small"
                                            @click="addRequirement"
                                        >
                                            新增條件
                                        </a-button>
                                    </div>
                                    <div
                                        v-if="
                                            formData.requirements.length === 0
                                        "
                                        class="empty-state"
                                    >
                                        <p>暫無升級條件，點擊上方按鈕新增</p>
                                    </div>
                                    <div v-else class="requirements-list">
                                        <div
                                            v-for="(
                                                req, index
                                            ) in formData.requirements"
                                            :key="index"
                                            class="requirement-item"
                                        >
                                            <a-row :gutter="16" align="middle">
                                                <a-col :span="6">
                                                    <a-select
                                                        v-model:value="req.type"
                                                        placeholder="條件類型"
                                                        :options="
                                                            requirementTypeOptions
                                                        "
                                                        class="w-full"
                                                        @change="
                                                            req.value = '';
                                                            req.count =
                                                                req.type ===
                                                                'point'
                                                                    ? 0
                                                                    : 1;
                                                        "
                                                    />
                                                </a-col>
                                                <a-col :span="6">
                                                    <!-- 點數類型選擇（當 type 為 point 時） -->
                                                    <a-select
                                                        v-if="
                                                            req.type === 'point'
                                                        "
                                                        v-model:value="
                                                            req.value
                                                        "
                                                        placeholder="選擇點數類型"
                                                        :options="
                                                            getRequirementValueOptions(
                                                                'point'
                                                            )
                                                        "
                                                        class="w-full"
                                                    />
                                                    <!-- 其他類型的值選擇 -->
                                                    <a-select
                                                        v-else-if="
                                                            req.type &&
                                                            getRequirementValueOptions(
                                                                req.type
                                                            ).length > 0
                                                        "
                                                        v-model:value="
                                                            req.value
                                                        "
                                                        placeholder="選擇數值/代碼"
                                                        :options="
                                                            getRequirementValueOptions(
                                                                req.type
                                                            )
                                                        "
                                                        class="w-full"
                                                    />
                                                    <a-input
                                                        v-else-if="req.type"
                                                        v-model:value="
                                                            req.value
                                                        "
                                                        placeholder="輸入數值/代碼"
                                                    />
                                                    <span
                                                        v-else
                                                        class="placeholder-text"
                                                    >
                                                        請先選擇條件類型
                                                    </span>
                                                </a-col>
                                                <a-col :span="4">
                                                    <a-input-number
                                                        v-model:value="
                                                            req.count
                                                        "
                                                        :min="
                                                            req.type === 'point'
                                                                ? 0
                                                                : 1
                                                        "
                                                        :placeholder="
                                                            req.type === 'point'
                                                                ? '所需數量'
                                                                : '數量'
                                                        "
                                                        class="w-full"
                                                    />
                                                </a-col>
                                                <a-col :span="6">
                                                    <a-input
                                                        v-model:value="req.note"
                                                        placeholder="備註（選填）"
                                                    />
                                                </a-col>
                                                <a-col :span="2">
                                                    <a-button
                                                        type="text"
                                                        class="delete-btn"
                                                        @click="
                                                            removeRequirement(
                                                                index
                                                            )
                                                        "
                                                    >
                                                        <template #icon>
                                                            <DeleteOutlined />
                                                        </template>
                                                    </a-button>
                                                </a-col>
                                            </a-row>
                                        </div>
                                    </div>
                                </div>
                            </a-card>
                        </a-collapse-panel>

                        <!-- 升級獎勵 -->
                        <a-collapse-panel
                            key="rewards"
                            class="form-section-panel"
                        >
                            <template #header>
                                <span class="section-header">升級獎勵</span>
                            </template>
                            <a-card
                                class="form-section-card-inner"
                                :bordered="false"
                            >
                                <div class="rewards-section">
                                    <div class="section-header">
                                        <div>
                                            <p class="section-desc">
                                                設定達到此等級時獲得的獎勵
                                            </p>
                                        </div>
                                        <a-button
                                            type="primary"
                                            size="small"
                                            disabled
                                        >
                                            新增獎勵
                                        </a-button>
                                    </div>
                                    <div
                                        v-if="formData.rewards.length === 0"
                                        class="empty-state"
                                    >
                                        <p>暫無升級獎勵，點擊上方按鈕新增</p>
                                    </div>
                                    <div v-else class="rewards-list">
                                        <div
                                            v-for="(
                                                reward, index
                                            ) in formData.rewards"
                                            :key="index"
                                            class="reward-item"
                                        >
                                            <a-row :gutter="16" align="middle">
                                                <a-col :span="6">
                                                    <a-select
                                                        v-model:value="
                                                            reward.type
                                                        "
                                                        placeholder="獎勵類型"
                                                        :options="
                                                            rewardTypeOptions
                                                        "
                                                        class="w-full"
                                                        disabled
                                                        @change="
                                                            reward.value = ''
                                                        "
                                                    />
                                                </a-col>
                                                <a-col :span="6">
                                                    <a-select
                                                        v-if="
                                                            reward.type &&
                                                            getRewardValueOptions(
                                                                reward.type
                                                            ).length > 0
                                                        "
                                                        v-model:value="
                                                            reward.value
                                                        "
                                                        placeholder="選擇數值/代碼"
                                                        :options="
                                                            getRewardValueOptions(
                                                                reward.type
                                                            )
                                                        "
                                                        class="w-full"
                                                        disabled
                                                    />
                                                    <a-input
                                                        v-else
                                                        v-model:value="
                                                            reward.value
                                                        "
                                                        placeholder="輸入數值/代碼"
                                                        disabled
                                                    />
                                                </a-col>
                                                <a-col :span="4">
                                                    <a-input-number
                                                        v-model:value="
                                                            reward.quantity
                                                        "
                                                        :min="1"
                                                        placeholder="數量"
                                                        class="w-full"
                                                        disabled
                                                    />
                                                </a-col>
                                                <a-col :span="6">
                                                    <a-input
                                                        v-model:value="
                                                            reward.note
                                                        "
                                                        placeholder="備註（選填）"
                                                        disabled
                                                    />
                                                </a-col>
                                                <a-col :span="2">
                                                    <a-button
                                                        type="text"
                                                        class="delete-btn"
                                                        disabled
                                                        @click="
                                                            removeReward(index)
                                                        "
                                                    >
                                                        <template #icon>
                                                            <DeleteOutlined />
                                                        </template>
                                                    </a-button>
                                                </a-col>
                                            </a-row>
                                        </div>
                                    </div>
                                </div>
                            </a-card>
                        </a-collapse-panel>
                    </a-collapse>
                </a-form>
            </div>
        </VaporwaveModal>

        <!-- 查看等級詳情 Modal -->
        <VaporwaveModal
            v-model:open="viewModalVisible"
            title="等級詳情"
            width="900px"
            :footer="null"
        >
            <div v-if="selectedLevel" class="level-detail">
                <a-descriptions bordered :column="1" class="level-descriptions">
                    <a-descriptions-item label="等級">
                        <div class="level-badge">
                            <span class="level-text">
                                Level {{ selectedLevel.level }}
                            </span>
                        </div>
                    </a-descriptions-item>
                    <a-descriptions-item label="解鎖說明">
                        <div class="description-text">
                            {{ selectedLevel.unlock_description || '無' }}
                        </div>
                    </a-descriptions-item>
                    <a-descriptions-item label="身份限制">
                        <div
                            v-if="selectedLevel.identity_limits?.length"
                            class="tags-container"
                        >
                            <a-tag
                                v-for="(
                                    identity, idx
                                ) in selectedLevel.identity_limits"
                                :key="idx"
                                color="blue"
                            >
                                {{
                                    typeof identity === 'object'
                                        ? identity.identity_name ||
                                          identity.label ||
                                          getIdentityLabel(
                                              identity.identity ||
                                                  identity.value
                                          )
                                        : getIdentityLabel(identity)
                                }}
                            </a-tag>
                        </div>
                        <span v-else class="no-data">無限制</span>
                    </a-descriptions-item>
                    <a-descriptions-item label="聘階限制">
                        <div
                            v-if="selectedLevel.rank_limits?.length"
                            class="tags-container"
                        >
                            <a-tag
                                v-for="rank in selectedLevel.rank_limits"
                                :key="rank.id || rank"
                                color="purple"
                            >
                                {{ rank.rank_name || rank.name || rank }}
                            </a-tag>
                        </div>
                        <span v-else class="no-data">無限制</span>
                    </a-descriptions-item>
                    <a-descriptions-item label="條件">
                        <div
                            v-if="selectedLevel.requirements?.length"
                            class="requirements-list"
                        >
                            <div
                                v-for="req in selectedLevel.requirements"
                                :key="
                                    req.id || req.requirement_value || req.value
                                "
                                class="requirement-item"
                            >
                                <a-tag color="orange">
                                    {{
                                        getRequirementTypeLabel(
                                            req.requirement_type || req.type
                                        )
                                    }}:
                                    {{
                                        (req.requirement_type || req.type) ===
                                        'point'
                                            ? req.point_type_code ||
                                              getPointTypeName(
                                                  req.requirement_value ||
                                                      req.value
                                              )
                                            : (req.requirement_type ||
                                                    req.type) === 'task'
                                              ? '任務'
                                              : req.requirement_value ||
                                                req.value
                                    }}
                                </a-tag>
                                <span
                                    v-if="
                                        (req.required_count !== undefined &&
                                            req.required_count !== null) ||
                                        (req.count !== undefined &&
                                            req.count !== null)
                                    "
                                    class="count-text"
                                >
                                    {{
                                        (req.requirement_type || req.type) ===
                                        'point'
                                            ? '所需數量'
                                            : '數量'
                                    }}:
                                    {{ req.required_count || req.count || 0 }}
                                </span>
                                <span v-if="req.note" class="note-text">
                                    ({{ req.note }})
                                </span>
                            </div>
                        </div>
                        <span v-else class="no-data">無條件</span>
                    </a-descriptions-item>
                    <a-descriptions-item label="獎勵">
                        <div
                            v-if="selectedLevel.rewards?.length"
                            class="rewards-list"
                        >
                            <div
                                v-for="reward in selectedLevel.rewards"
                                :key="
                                    reward.id ||
                                    reward.reward_value ||
                                    reward.value
                                "
                                class="reward-item"
                            >
                                <a-tag color="green">
                                    {{
                                        getRewardTypeLabel(
                                            reward.reward_type || reward.type
                                        )
                                    }}:
                                    {{ reward.reward_value || reward.value }}
                                </a-tag>
                                <span
                                    v-if="
                                        reward.quantity !== undefined ||
                                        reward.amount !== undefined
                                    "
                                    class="quantity-text"
                                >
                                    數量:
                                    {{ reward.quantity || reward.amount || 1 }}
                                </span>
                                <span v-if="reward.note" class="note-text">
                                    ({{ reward.note }})
                                </span>
                            </div>
                        </div>
                        <span v-else class="no-data">無獎勵</span>
                    </a-descriptions-item>
                    <a-descriptions-item label="創建時間">
                        <span class="date-text">
                            {{ formatDate(selectedLevel.created_at) }}
                        </span>
                    </a-descriptions-item>
                    <a-descriptions-item label="更新時間">
                        <span class="date-text">
                            {{ formatDate(selectedLevel.updated_at) }}
                        </span>
                    </a-descriptions-item>
                </a-descriptions>
            </div>
        </VaporwaveModal>

        <!-- 刪除確認 Modal -->
        <VaporwaveModal
            v-model:open="deleteModalVisible"
            title="刪除等級設定"
            width="600px"
            :confirm-loading="confirmLoading"
            @ok="deleteLevel"
            @cancel="
                () => {
                    levelToDelete = null;
                }
            "
        >
            <div v-if="levelToDelete" class="delete-confirm-content">
                <a-alert type="warning" show-icon class="delete-warning-alert">
                    <template #message>
                        <span class="warning-text">
                            確定要刪除此等級設定嗎？此操作無法復原。
                        </span>
                    </template>
                </a-alert>
                <div class="level-info-preview">
                    <a-descriptions
                        bordered
                        :column="1"
                        size="small"
                        class="level-preview-descriptions"
                    >
                        <a-descriptions-item label="等級">
                            <div class="level-badge-small">
                                <span class="level-text-small">
                                    Lv.{{ levelToDelete.level }}
                                </span>
                            </div>
                        </a-descriptions-item>
                        <a-descriptions-item label="解鎖說明">
                            <div class="description-text-small">
                                {{ levelToDelete.unlock_description || '無' }}
                            </div>
                        </a-descriptions-item>
                        <a-descriptions-item label="條件">
                            <div
                                v-if="levelToDelete.requirements?.length"
                                class="tags-container-small"
                            >
                                <a-tag
                                    v-for="(
                                        req, idx
                                    ) in levelToDelete.requirements"
                                    :key="idx"
                                    color="orange"
                                    size="small"
                                >
                                    {{
                                        (req.requirement_type || req.type) ===
                                            'point' && req.point_type_code
                                            ? `${req.required_count || req.count || 0}${req.point_type_code}`
                                            : (req.requirement_type ||
                                                    req.type) === 'task'
                                              ? '任務'
                                              : '條件'
                                    }}
                                </a-tag>
                            </div>
                            <span v-else class="no-data-text-small">
                                無條件
                            </span>
                        </a-descriptions-item>
                        <a-descriptions-item label="獎勵">
                            <div
                                v-if="levelToDelete.rewards?.length"
                                class="tags-container-small"
                            >
                                <a-tag color="green" size="small">有獎勵</a-tag>
                            </div>
                            <span v-else class="no-data-text-small">
                                無獎勵
                            </span>
                        </a-descriptions-item>
                    </a-descriptions>
                </div>
            </div>
        </VaporwaveModal>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue';
import { message, notification } from 'ant-design-vue';
import { showErrorMessage } from '@/utils/messageHelper';
import { useAdminInfo } from '@/stores/useAdminInfo';
import {
    TrophyOutlined,
    DeleteOutlined,
    AppstoreOutlined,
    UnorderedListOutlined,
    DownOutlined,
    UpOutlined,
    EditOutlined
} from '@ant-design/icons-vue';
import DataTable from '@/components/common/DataTable.vue';
import EditModal from '@/components/common/EditModal.vue';
import SearchBar from '@/components/common/SearchBar.vue';
import VaporwaveModal from '@/components/common/VaporwaveModal.vue';
import {
    getLevelConfigs,
    getLevelConfig,
    updateLevelConfig,
    deleteLevel as deleteLevelAPI,
    createLevelConfig,
    getLevelConfigOptions
} from '@/api/levels';

// 響應式數據
const loading = ref(false);
const confirmLoading = ref(false);
const editModalVisible = ref(false);
const viewModalVisible = ref(false);
const deleteModalVisible = ref(false);
const isEditMode = ref(false);
const levelToDelete = ref(null);
const searchText = ref('');
const levelsList = ref([]);
const adminStore = useAdminInfo();

// 權限檢查
const canView = computed(() => adminStore.hasPermissionCode('120101')); // 查看等級設定
const canCreate = computed(() => adminStore.hasPermissionCode('120102')); // 新增等級設定
const canEdit = computed(() => adminStore.hasPermissionCode('120103')); // 修改等級設定
const canDelete = computed(() => adminStore.hasPermissionCode('120104')); // 刪除等級設定

const selectedLevel = ref(null);
const formRef = ref();
const basicFormRef = ref();
const currentSort = ref('level');
const currentOrder = ref('asc');
const sectionsActiveKeys = ref(['basic', 'limits', 'requirements', 'rewards']);
const viewMode = ref('card'); // 'card' 或 'group'
const allLevelsList = ref([]); // 所有等級資料（用於卡片視圖）
const cardViewPagination = reactive({
    current: 1,
    pageSize: 50,
    total: 0,
    hasMore: false
});
const allGroupsList = ref([]); // 所有等級資料（用於分組視圖）
const groupViewPagination = reactive({
    current: 1,
    pageSize: 50,
    total: 0,
    hasMore: false
});
const expandedGroups = ref(new Set()); // 追蹤展開的分組 key

// 篩選參數
const filters = reactive({
    level_min: null,
    level_max: null,
    identities: [],
    rank_ids: []
});

// 分頁設定
const pagination = reactive({
    current: 1,
    pageSize: 50,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `共 ${total} 條記錄`
});

// 表單數據
const formData = reactive({
    level: {
        level: null,
        unlock_description: ''
    },
    identities: [],
    rank_ids: [],
    requirements: [],
    rewards: []
});

// 表單驗證規則
const basicFormRules = {
    level: [
        { required: true, message: '請輸入等級', trigger: 'blur' },
        {
            type: 'number',
            min: 1,
            max: 999,
            message: '等級必須在 1-999 之間',
            trigger: 'blur'
        }
    ],
    unlock_description: [
        { max: 500, message: '解鎖說明不能超過 500 個字符', trigger: 'blur' }
    ]
};

// 選項數據
const identityLimitOptions = ref([]); // 身份限制選項（角色列表）
const rankOptions = ref([]);
const requirementTypeOptions = ref([]);
const rewardTypeOptions = ref([]);
// 配置選項資料
const configOptions = ref({
    requirements: {
        types: {},
        options: {}
    },
    rewards: {
        types: {},
        options: {}
    },
    identity_limits: []
});

// 取得升級條件的選項（根據類型）
const getRequirementValueOptions = (type) => {
    if (!type || !configOptions.value.requirements.options[type]) {
        return [];
    }
    const options = configOptions.value.requirements.options[type];

    // 如果是 point 類型，使用 code 作為 label
    if (type === 'point') {
        return options.map((opt) => ({
            value: opt.value,
            label: opt.code || opt.label || opt.value
        }));
    }

    return options;
};

// 取得獎勵的選項（根據類型）
const getRewardValueOptions = (type) => {
    if (!type || !configOptions.value.rewards.options[type]) {
        return [];
    }
    return configOptions.value.rewards.options[type];
};

// 取得身份限制選項（現在只有角色）
const getIdentityLimitOptions = () => {
    return identityLimitOptions.value;
};

// 計算身份分組（只包含沒有聘階限制的身份等級）
const identityGroups = computed(() => {
    const groups = {};
    const sourceList =
        viewMode.value === 'group' ? allGroupsList.value : levelsList.value;

    sourceList.forEach((level) => {
        // 只處理有身份限制但沒有聘階限制的等級
        if (
            level.identity_limits &&
            level.identity_limits.length > 0 &&
            (!level.rank_limits || level.rank_limits.length === 0)
        ) {
            level.identity_limits.forEach((identity) => {
                // 處理不同格式：字串或物件
                const identityKey =
                    typeof identity === 'string'
                        ? identity
                        : identity.identity || identity.value || identity;
                const identityLabel =
                    typeof identity === 'string'
                        ? getIdentityLabel(identity)
                        : identity.identity_name ||
                          identity.label ||
                          getIdentityLabel(identityKey);

                if (!groups[identityKey]) {
                    groups[identityKey] = {
                        key: identityKey,
                        label: identityLabel,
                        levels: []
                    };
                }
                groups[identityKey].levels.push(level);
            });
        }
    });

    return Object.values(groups)
        .map((group) => {
            const sortedLevels = [...group.levels].sort(
                (a, b) => a.level - b.level
            );
            return {
                ...group,
                levels: sortedLevels,
                minLevel: sortedLevels[0]?.level || 0,
                maxLevel: sortedLevels[sortedLevels.length - 1]?.level || 0
            };
        })
        .sort((a, b) => a.minLevel - b.minLevel);
});

// 計算聘階分組（包含身份和聘階的組合，按身份+聘階分組）
const rankGroups = computed(() => {
    const groups = {};
    const sourceList =
        viewMode.value === 'group' ? allGroupsList.value : levelsList.value;

    sourceList.forEach((level) => {
        // 只處理有聘階限制的等級
        if (level.rank_limits && level.rank_limits.length > 0) {
            // 取得身份標籤（如果有的話）
            const identityLabel =
                level.identity_limits && level.identity_limits.length > 0
                    ? typeof level.identity_limits[0] === 'object'
                        ? level.identity_limits[0].identity_name ||
                          level.identity_limits[0].label
                        : getIdentityLabel(level.identity_limits[0])
                    : '';

            level.rank_limits.forEach((rank) => {
                const rankId = rank.rank_id || rank.id || rank;
                const rankName =
                    rank.rank_name || rank.name || `聘階 ${rankId}`;

                // 組合 key：身份 + 聘階
                const groupKey = identityLabel
                    ? `${identityLabel}_${rankId}`
                    : `rank_${rankId}`;
                const groupLabel = identityLabel
                    ? `${identityLabel} - ${rankName}`
                    : rankName;

                if (!groups[groupKey]) {
                    groups[groupKey] = {
                        key: groupKey,
                        label: groupLabel,
                        rankId: rankId,
                        rankName: rankName,
                        identityLabel: identityLabel,
                        levels: []
                    };
                }
                groups[groupKey].levels.push(level);
            });
        }
    });

    return Object.values(groups)
        .map((group) => {
            const sortedLevels = [...group.levels].sort(
                (a, b) => a.level - b.level
            );
            return {
                ...group,
                levels: sortedLevels,
                minLevel: sortedLevels[0]?.level || 0,
                maxLevel: sortedLevels[sortedLevels.length - 1]?.level || 0
            };
        })
        .sort((a, b) => a.minLevel - b.minLevel);
});

// 計算無限制等級（沒有身份和聘階限制的等級）
const unrestrictedLevels = computed(() => {
    const sourceList =
        viewMode.value === 'group' ? allGroupsList.value : levelsList.value;
    return sourceList
        .filter(
            (level) =>
                (!level.identity_limits ||
                    level.identity_limits.length === 0) &&
                (!level.rank_limits || level.rank_limits.length === 0)
        )
        .sort((a, b) => a.level - b.level);
});

// 過濾後的等級列表（用於卡片視圖 - 顯示所有資料，不分頁）
const filteredLevelsList = computed(() => {
    // 卡片視圖使用所有資料，不進行分頁過濾
    let filtered = [...allLevelsList.value];

    // 搜尋過濾
    if (searchText.value) {
        const search = searchText.value.toLowerCase();
        filtered = filtered.filter(
            (level) =>
                level.level.toString().includes(search) ||
                (level.unlock_description &&
                    level.unlock_description.toLowerCase().includes(search))
        );
    }

    // 按等級排序
    return filtered.sort((a, b) => a.level - b.level);
});

// 表格列配置
const columns = [
    {
        title: '等級',
        dataIndex: 'level',
        key: 'level',
        width: 120,
        sorter: true
    },
    {
        title: '解鎖說明',
        dataIndex: 'unlock_description',
        key: 'unlock_description',
        width: 250,
        ellipsis: true
    },
    {
        title: '設定摘要',
        key: 'summary',
        width: 300
    },
    {
        title: '創建時間',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 180,
        sorter: true
    },
    {
        title: '更新時間',
        dataIndex: 'updated_at',
        key: 'updated_at',
        width: 180,
        sorter: true
    },
    {
        title: '操作',
        key: 'actions',
        width: 200,
        fixed: 'right'
    }
];

// 方法
// 加載等級列表
const loadLevels = async () => {
    try {
        loading.value = true;
        const params = {
            page: pagination.current,
            per_page: pagination.pageSize,
            sort: currentSort.value || 'level',
            order: currentOrder.value || 'asc'
        };

        if (searchText.value) {
            params.search = searchText.value;
        }

        // 添加篩選參數
        if (filters.level_min !== null && filters.level_min !== undefined) {
            params.level_min = filters.level_min;
        }
        if (filters.level_max !== null && filters.level_max !== undefined) {
            params.level_max = filters.level_max;
        }
        if (filters.identities && filters.identities.length > 0) {
            params.identities = filters.identities.join(',');
        }
        if (filters.rank_ids && filters.rank_ids.length > 0) {
            params.rank_ids = filters.rank_ids.join(',');
        }

        const response = await getLevelConfigs(params);

        if (response.result) {
            levelsList.value = response.data?.data || [];
            pagination.total = response.data?.pagination?.total || 0;
            pagination.current = response.data?.pagination?.current_page || 1;
            pagination.pageSize = response.data?.pagination?.per_page || 15;

            // 根據視圖模式載入資料
            if (viewMode.value === 'card') {
                await loadCardViewLevels(false);
            } else if (viewMode.value === 'group') {
                await loadGroupViewLevels(false);
            }
        } else {
            showErrorMessage(response.message || '加載等級列表失敗');
        }
    } catch (error) {
        console.error('Load levels error:', error);
        showErrorMessage('加載等級列表失敗');
    } finally {
        loading.value = false;
    }
};

// 載入卡片視圖的等級資料（分頁載入）
const loadCardViewLevels = async (isLoadMore = false) => {
    try {
        if (!isLoadMore) {
            // 首次載入或重置，清空列表
            allLevelsList.value = [];
            cardViewPagination.current = 1;
        }

        const params = {
            page: cardViewPagination.current,
            per_page: cardViewPagination.pageSize,
            sort: currentSort.value || 'level',
            order: currentOrder.value || 'asc'
        };

        // 添加篩選參數
        if (filters.level_min !== null && filters.level_min !== undefined) {
            params.level_min = filters.level_min;
        }
        if (filters.level_max !== null && filters.level_max !== undefined) {
            params.level_max = filters.level_max;
        }
        if (filters.identities && filters.identities.length > 0) {
            params.identities = filters.identities.join(',');
        }
        if (filters.rank_ids && filters.rank_ids.length > 0) {
            params.rank_ids = filters.rank_ids.join(',');
        }

        const response = await getLevelConfigs(params);
        if (response.result) {
            const newData = response.data?.data || [];
            if (isLoadMore) {
                // 載入更多時，追加到現有列表
                allLevelsList.value = [...allLevelsList.value, ...newData];
            } else {
                // 首次載入或重置時，替換列表
                allLevelsList.value = newData;
            }

            const paginationData = response.data?.pagination || {};
            cardViewPagination.total = paginationData.total || 0;
            cardViewPagination.current = paginationData.current_page || 1;
            cardViewPagination.hasMore =
                cardViewPagination.current < (paginationData.last_page || 1);
        }
    } catch (error) {
        console.error('Load card view levels error:', error);
    }
};

// 載入更多（卡片視圖）
const handleLoadMore = async () => {
    cardViewPagination.current += 1;
    await loadCardViewLevels(true);
};

// 載入分組視圖等級資料
const loadGroupViewLevels = async (isLoadMore = false) => {
    try {
        if (!isLoadMore) {
            // 首次載入或重置，清空列表
            allGroupsList.value = [];
            groupViewPagination.current = 1;
        }

        const params = {
            page: groupViewPagination.current,
            per_page: groupViewPagination.pageSize,
            sort: currentSort.value || 'level',
            order: currentOrder.value || 'asc'
        };

        // 添加篩選參數
        if (filters.level_min !== null && filters.level_min !== undefined) {
            params.level_min = filters.level_min;
        }
        if (filters.level_max !== null && filters.level_max !== undefined) {
            params.level_max = filters.level_max;
        }
        if (filters.identities && filters.identities.length > 0) {
            params.identities = filters.identities.join(',');
        }
        if (filters.rank_ids && filters.rank_ids.length > 0) {
            params.rank_ids = filters.rank_ids.join(',');
        }

        const response = await getLevelConfigs(params);
        if (response.result) {
            const newData = response.data?.data || [];
            if (isLoadMore) {
                // 載入更多時，追加到現有列表
                allGroupsList.value = [...allGroupsList.value, ...newData];
            } else {
                // 首次載入或重置時，替換列表
                allGroupsList.value = newData;
            }

            const paginationData = response.data?.pagination || {};
            groupViewPagination.total = paginationData.total || 0;
            groupViewPagination.current = paginationData.current_page || 1;
            groupViewPagination.hasMore =
                groupViewPagination.current < (paginationData.last_page || 1);
        }
    } catch (error) {
        console.error('Load group view levels error:', error);
    }
};

// 載入更多（分組視圖）
const handleGroupViewLoadMore = async () => {
    groupViewPagination.current += 1;
    await loadGroupViewLevels(true);
};

// 刷新列表
const handleRefresh = () => {
    loadLevels();
};

// 搜索
const handleSearch = () => {
    pagination.current = 1;
    loadLevels();
};

// 排序變更
const handleSortChange = () => {
    pagination.current = 1;
    if (viewMode.value === 'card') {
        loadCardViewLevels(false);
    } else if (viewMode.value === 'group') {
        loadGroupViewLevels(false);
    } else {
        loadLevels();
    }
};

// 排序方向變更
const handleOrderChange = (order) => {
    currentOrder.value = order;
    pagination.current = 1;
    if (viewMode.value === 'card') {
        loadCardViewLevels(false);
    } else if (viewMode.value === 'group') {
        loadGroupViewLevels(false);
    } else {
        loadLevels();
    }
};

// 分頁變更
const handlePaginationChange = (page, pageSize) => {
    pagination.current = page;
    pagination.pageSize = pageSize;
    loadLevels();
};

// 篩選變更
const handleFilterChange = () => {
    pagination.current = 1; // 重置到第一頁
    if (viewMode.value === 'card') {
        loadCardViewLevels(false);
    } else if (viewMode.value === 'group') {
        loadGroupViewLevels(false);
    } else {
        loadLevels();
    }
};

// 重置篩選
const handleResetFilters = () => {
    filters.level_min = null;
    filters.level_max = null;
    filters.identities = [];
    filters.rank_ids = [];
    pagination.current = 1;
    if (viewMode.value === 'card') {
        loadCardViewLevels(false);
    } else if (viewMode.value === 'group') {
        loadGroupViewLevels(false);
    } else {
        loadLevels();
    }
};

// 篩選選項（身份）
const identityFilterOptions = computed(() => {
    return identityLimitOptions.value.map((item) => ({
        label: item.label,
        value: item.value
    }));
});

// 篩選選項（聘階）
const rankFilterOptions = computed(() => {
    return rankOptions.value.map((item) => ({
        label: item.label,
        value: item.value
    }));
});

// 表格變化
const handleTableChange = (pag, filters, sorter) => {
    pagination.current = pag.current;
    pagination.pageSize = pag.pageSize;

    // 處理排序
    if (sorter && sorter.field) {
        currentSort.value = sorter.field;
        currentOrder.value = sorter.order === 'ascend' ? 'asc' : 'desc';
    }

    loadLevels();
};

// 顯示新增模態框
const showCreateModal = () => {
    isEditMode.value = false;
    resetForm();
    sectionsActiveKeys.value = ['basic'];
    editModalVisible.value = true;
};

// 編輯等級
const editLevel = async (level) => {
    try {
        loading.value = true;
        const response = await getLevelConfig(level.id);
        if (response.result) {
            isEditMode.value = true;
            selectedLevel.value = response.data;

            // 設定表單數據
            formData.level = {
                level: response.data.level,
                unlock_description: response.data.unlock_description || ''
            };
            // 處理身份限制資料（API 返回物件陣列，包含 identity 和 identity_name）
            if (response.data.identity_limits) {
                if (Array.isArray(response.data.identity_limits)) {
                    // 如果是字串陣列，直接使用
                    if (typeof response.data.identity_limits[0] === 'string') {
                        formData.identities = [
                            ...response.data.identity_limits
                        ];
                    } else {
                        // 如果是物件陣列，提取 identity（API 返回的是 identity 欄位）
                        formData.identities = response.data.identity_limits.map(
                            (item) =>
                                typeof item === 'string'
                                    ? item
                                    : item.identity || item.value
                        );
                    }
                } else {
                    formData.identities = [];
                }
            } else {
                formData.identities = [];
            }
            // 處理聘階限制資料（API 可能返回 rank_id 或 id）
            // 確保類型與 rankOptions 的 value 一致（數字）
            formData.rank_ids =
                response.data.rank_limits?.map((rank) =>
                    Number(rank.rank_id || rank.id)
                ) || [];

            // 處理 requirements（API 回傳格式：requirement_type, requirement_value, required_count）
            const requirements = response.data.requirements || [];
            formData.requirements = requirements.map((req) => ({
                type: req.requirement_type || req.type,
                value: req.requirement_value || req.value,
                count: req.required_count || req.count || 0,
                note: req.note || ''
            }));

            // 處理 rewards（API 回傳格式可能不同，需要轉換）
            const rewards = response.data.rewards || [];
            formData.rewards = rewards.map((reward) => ({
                type: reward.reward_type || reward.type,
                value: reward.reward_value || reward.value,
                quantity: reward.quantity || reward.amount || 1,
                note: reward.note || ''
            }));

            editModalVisible.value = true;
            sectionsActiveKeys.value = ['basic'];
        } else {
            showErrorMessage(response.message || '獲取等級詳情失敗');
        }
    } catch (error) {
        console.error('Get level detail error:', error);
        showErrorMessage('獲取等級詳情失敗');
    } finally {
        loading.value = false;
    }
};

// 查看等級詳情
// 根據等級數字取得等級物件
const getLevelByNumber = (levelNumber) => {
    return levelsList.value.find((l) => l.level === levelNumber);
};

// 根據等級數字查看等級詳情
const viewLevelByNumber = async (levelNumber) => {
    const level = getLevelByNumber(levelNumber);
    if (level) {
        await viewLevel(level);
    }
};

const viewLevel = async (level) => {
    try {
        loading.value = true;
        const response = await getLevelConfig(level.id);
        if (response.result) {
            selectedLevel.value = response.data;
            viewModalVisible.value = true;
        } else {
            showErrorMessage(response.message || '獲取等級詳情失敗');
        }
    } catch (error) {
        console.error('Get level detail error:', error);
        showErrorMessage('獲取等級詳情失敗');
    } finally {
        loading.value = false;
    }
};

// 打開刪除確認 Modal
const openDeleteModal = async (level) => {
    try {
        loading.value = true;
        // 獲取完整的等級資料
        const response = await getLevelConfig(level.id);
        if (response.result) {
            levelToDelete.value = response.data;
            deleteModalVisible.value = true;
        } else {
            showErrorMessage(response.message || '獲取等級資料失敗');
        }
    } catch (error) {
        console.error('Get level detail error:', error);
        showErrorMessage('獲取等級資料失敗');
    } finally {
        loading.value = false;
    }
};

// 刪除等級
const deleteLevel = async () => {
    if (!levelToDelete.value) return;

    try {
        confirmLoading.value = true;
        const response = await deleteLevelAPI(levelToDelete.value.id);
        if (response.result) {
            message.success('刪除成功');
            deleteModalVisible.value = false;
            levelToDelete.value = null;
            loadLevels();
        } else {
            showErrorMessage(response.message || '刪除失敗');
        }
    } catch (error) {
        console.error('Delete level error:', error);
        showErrorMessage('刪除失敗');
    } finally {
        confirmLoading.value = false;
    }
};

// 保存等級
const handleSave = async () => {
    try {
        await basicFormRef.value.validate();
        confirmLoading.value = true;

        // 準備保存資料
        const saveData = {
            level: formData.level,
            // 身份限制現在是字串陣列：["student", "partner"]
            identities: formData.identities.filter(
                (identity) => identity && identity.trim() !== ''
            ),
            rank_ids: formData.rank_ids,
            requirements: formData.requirements.filter(
                (req) =>
                    req.type &&
                    req.value &&
                    req.count !== undefined &&
                    req.count !== null
            ),
            rewards: formData.rewards
                .filter(
                    (reward) =>
                        reward.type &&
                        reward.value &&
                        reward.quantity !== undefined &&
                        reward.quantity !== null
                )
                .map((reward) => ({
                    type: reward.type,
                    value: reward.value,
                    quantity: reward.quantity || 1,
                    note: reward.note || ''
                }))
        };

        let response;
        try {
            if (isEditMode.value) {
                response = await updateLevelConfig(
                    selectedLevel.value.id,
                    saveData
                );
            } else {
                response = await createLevelConfig(saveData);
            }
        } catch (apiError) {
            // API 錯誤會被 handleHttpError 處理，但我們需要檢查是否有驗證錯誤
            const errorData = apiError?.response?.data || apiError?.data;

            // 如果是 422 驗證錯誤，不讓 handleHttpError 顯示通用錯誤
            if (errorData?.errors && errorData.status === 0) {
                // 處理驗證錯誤
                const errorMessages = [];
                const formErrors = {};

                Object.keys(errorData.errors).forEach((field) => {
                    const fieldErrors = errorData.errors[field];
                    if (Array.isArray(fieldErrors)) {
                        const formField = field.includes('.')
                            ? field.split('.')[0]
                            : field;

                        const translatedErrors = fieldErrors.map((err) =>
                            translateErrorMessage(err)
                        );

                        formErrors[formField] = {
                            errors: translatedErrors
                        };

                        const fieldLabel = getFieldLabel(field);
                        translatedErrors.forEach((err) => {
                            errorMessages.push(`${fieldLabel}：${err}`);
                        });
                    }
                });

                // 設置表單錯誤
                if (Object.keys(formErrors).length > 0 && basicFormRef.value) {
                    try {
                        if (
                            typeof basicFormRef.value.setFields === 'function'
                        ) {
                            basicFormRef.value.setFields(formErrors);
                        }
                    } catch (setFieldsError) {
                        console.warn('設置表單錯誤失敗:', setFieldsError);
                    }
                }

                // 顯示錯誤通知
                notification.error({
                    message: isEditMode.value ? '更新失敗' : '新增失敗',
                    description:
                        errorMessages.length > 0
                            ? errorMessages.join('<br/>')
                            : translateErrorMessage(errorData.message) ||
                              '請檢查輸入的資料',
                    duration: 5,
                    placement: 'topRight',
                    style: {
                        width: '400px'
                    }
                });

                return; // 提前返回，不繼續執行
            }

            // 其他錯誤讓 handleHttpError 處理
            throw apiError;
        }

        if (response && response.result) {
            message.success(isEditMode.value ? '更新成功' : '新增成功');
            editModalVisible.value = false;
            loadLevels();
        } else {
            // 處理驗證錯誤
            let errorTitle = isEditMode.value ? '更新失敗' : '新增失敗';
            let errorDescription = response.message || '請檢查輸入的資料';

            // 如果有 errors 物件，處理欄位錯誤
            if (response.errors && typeof response.errors === 'object') {
                const errorMessages = [];
                const formErrors = {};

                // 收集所有錯誤訊息
                Object.keys(response.errors).forEach((field) => {
                    const fieldErrors = response.errors[field];
                    if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
                        // 將欄位名稱轉換為表單欄位名稱
                        // level.level -> level
                        const formField = field.includes('.')
                            ? field.split('.')[0]
                            : field;

                        // 翻譯錯誤訊息並設置到表單
                        const translatedErrors = fieldErrors.map((err) =>
                            translateErrorMessage(err)
                        );

                        formErrors[formField] = {
                            errors: translatedErrors
                        };

                        // 收集錯誤訊息（帶欄位名稱）
                        const fieldLabel = getFieldLabel(field);
                        translatedErrors.forEach((err) => {
                            errorMessages.push(`${fieldLabel}：${err}`);
                        });
                    }
                });

                // 設置表單錯誤
                if (Object.keys(formErrors).length > 0 && basicFormRef.value) {
                    try {
                        if (
                            typeof basicFormRef.value.setFields === 'function'
                        ) {
                            basicFormRef.value.setFields(formErrors);
                        }
                    } catch (setFieldsError) {
                        console.warn('設置表單錯誤失敗:', setFieldsError);
                    }
                }

                // 顯示錯誤訊息
                if (errorMessages.length > 0) {
                    errorDescription = errorMessages.join('<br/>');
                }
            } else if (response.message) {
                errorDescription = translateErrorMessage(response.message);
            }

            // 使用 notification 統一顯示錯誤
            notification.error({
                message: errorTitle,
                description: errorDescription,
                duration: 5,
                placement: 'topRight',
                style: {
                    width: '400px'
                }
            });
        }
    } catch (error) {
        console.error('Save level error:', error);
        if (!error.errorFields) {
            // 檢查 error.response 中是否有 errors（422 驗證錯誤）
            const errorData = error.response?.data || error.data;

            if (errorData?.errors && typeof errorData.errors === 'object') {
                const errorMessages = [];
                const formErrors = {};

                Object.keys(errorData.errors).forEach((field) => {
                    const fieldErrors = errorData.errors[field];
                    if (Array.isArray(fieldErrors)) {
                        // 將欄位名稱轉換為表單欄位名稱
                        const formField = field.includes('.')
                            ? field.split('.')[0]
                            : field;

                        // 翻譯錯誤訊息
                        const translatedErrors = fieldErrors.map((err) =>
                            translateErrorMessage(err)
                        );

                        formErrors[formField] = {
                            errors: translatedErrors
                        };

                        // 收集錯誤訊息
                        const fieldLabel = getFieldLabel(field);
                        translatedErrors.forEach((err) => {
                            errorMessages.push(`${fieldLabel}：${err}`);
                        });
                    }
                });

                // 設置表單錯誤
                if (Object.keys(formErrors).length > 0 && basicFormRef.value) {
                    try {
                        if (
                            typeof basicFormRef.value.setFields === 'function'
                        ) {
                            basicFormRef.value.setFields(formErrors);
                        }
                    } catch (setFieldsError) {
                        console.warn('設置表單錯誤失敗:', setFieldsError);
                    }
                }

                // 顯示錯誤通知
                if (errorMessages.length > 0) {
                    notification.error({
                        message: isEditMode.value ? '更新失敗' : '新增失敗',
                        description: errorMessages.join('<br/>'),
                        duration: 5,
                        placement: 'topRight',
                        style: {
                            width: '400px'
                        }
                    });
                } else {
                    notification.error({
                        message: isEditMode.value ? '更新失敗' : '新增失敗',
                        description:
                            translateErrorMessage(errorData.message) ||
                            '請檢查輸入的資料',
                        duration: 5,
                        placement: 'topRight',
                        style: {
                            width: '400px'
                        }
                    });
                }
            } else {
                // 一般錯誤
                const errorMsg =
                    errorData?.message ||
                    error.message ||
                    '發生未知錯誤，請稍後重試';
                notification.error({
                    message: isEditMode.value ? '更新失敗' : '新增失敗',
                    description: translateErrorMessage(errorMsg),
                    duration: 5,
                    placement: 'topRight',
                    style: {
                        width: '400px'
                    }
                });
            }
        }
    } finally {
        confirmLoading.value = false;
    }
};

// 取消編輯
const handleCancel = () => {
    editModalVisible.value = false;
    resetForm();
};

// 新增升級條件
const addRequirement = () => {
    formData.requirements.push({
        type: '',
        value: '',
        count: 1,
        note: ''
    });
};

// 移除升級條件
const removeRequirement = (index) => {
    formData.requirements.splice(index, 1);
};

// 新增升級獎勵
const addReward = () => {
    formData.rewards.push({
        type: '',
        value: '',
        quantity: 1,
        note: ''
    });
};

// 移除升級獎勵
const removeReward = (index) => {
    formData.rewards.splice(index, 1);
};

// 切換分組展開/收合
const toggleGroupExpand = (groupKey) => {
    if (expandedGroups.value.has(groupKey)) {
        expandedGroups.value.delete(groupKey);
    } else {
        expandedGroups.value.add(groupKey);
    }
};

// 新增身份限制
const addIdentityLimit = () => {
    formData.identities.push({
        type: '',
        value: '',
        label: ''
    });
};

// 移除身份限制
const removeIdentityLimit = (index) => {
    formData.identities.splice(index, 1);
};

// 載入選項資料
const loadOptions = async () => {
    // 載入配置選項（升級條件和獎勵，包含 point 選項，以及身份和聘階選項）
    try {
        const configResponse = await getLevelConfigOptions();
        if (configResponse.success || configResponse.result) {
            const data = configResponse.data;

            // 調試：輸出完整的 API 回應
            console.log('Config options response:', data);
            console.log('All keys in data:', Object.keys(data || {}));

            // 儲存完整配置選項
            if (data?.requirements) {
                configOptions.value.requirements = data.requirements;
                // 設定升級條件類型選項
                if (data.requirements.types) {
                    requirementTypeOptions.value = Object.entries(
                        data.requirements.types
                    ).map(([value, label]) => ({
                        label,
                        value
                    }));
                }
            }

            if (data?.rewards) {
                configOptions.value.rewards = data.rewards;
                // 設定獎勵類型選項
                if (data.rewards.types) {
                    rewardTypeOptions.value = Object.entries(
                        data.rewards.types
                    ).map(([value, label]) => ({
                        label,
                        value
                    }));
                }
            }

            // 載入身份限制選項（現在只有角色）
            if (data?.identity_limits) {
                configOptions.value.identity_limits = data.identity_limits;
                identityLimitOptions.value = data.identity_limits.map(
                    (item) => ({
                        label: item.label,
                        value: item.value
                    })
                );
            }

            // 載入聘階選項（從 data.rank_limits）
            if (data?.rank_limits && Array.isArray(data.rank_limits)) {
                rankOptions.value = data.rank_limits.map((rank) => ({
                    label:
                        rank.label ||
                        rank.name ||
                        rank.rank_name ||
                        `聘階 ${rank.value}`,
                    value: Number(rank.value) // API 返回的 value 就是聘階 ID
                }));
            } else {
                rankOptions.value = [];
            }
        }
    } catch (error) {
        console.error('Load config options error:', error);
    }
};

// 重置表單
const resetForm = () => {
    formData.level = {
        level: null,
        unlock_description: ''
    };
    formData.identities = [];
    formData.rank_ids = [];
    formData.requirements = [];
    formData.rewards = [];
    selectedLevel.value = null;
    if (basicFormRef.value) {
        basicFormRef.value.resetFields();
    }
};

// 格式化日期
const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('zh-TW');
};

// 取得身份標籤
const getIdentityLabel = (identity) => {
    const identityMap = {
        guest: '訪客',
        student: '學員',
        partner: '合作夥伴'
    };
    return identityMap[identity] || identity;
};

// 取得升級條件類型標籤
const getRequirementTypeLabel = (type) => {
    const typeMap = {
        point: '點數',
        task: '任務完成'
    };
    return typeMap[type] || type;
};

// 取得點數類型名稱（顯示 code）
const getPointTypeName = (pointTypeId) => {
    if (!pointTypeId) return '';
    // 先從 requirements.options.point 中查找
    const rawOptions = configOptions.value.requirements.options?.point || [];
    const pointType = rawOptions.find(
        (opt) => opt.value === pointTypeId || opt.value === String(pointTypeId)
    );
    if (pointType) {
        // 優先顯示 code，如果沒有則顯示 label
        return pointType.code || pointType.label || `點數類型 ${pointTypeId}`;
    }
    return `點數類型 ${pointTypeId}`;
};

// 取得獎勵類型標籤
const getRewardTypeLabel = (type) => {
    const typeMap = {
        points: '點數獎勵',
        item: '道具獎勵',
        unlock: '解鎖功能'
    };
    return typeMap[type] || type;
};

// 翻譯錯誤訊息
const translateErrorMessage = (errorMsg) => {
    if (!errorMsg || typeof errorMsg !== 'string') {
        return errorMsg;
    }

    // 錯誤訊息翻譯對照表
    const errorTranslations = {
        'The level.level has already been taken.':
            '此等級已被使用，請選擇其他等級',
        'The level has already been taken.': '此等級已被使用，請選擇其他等級',
        'has already been taken': '已被使用',
        'is required': '為必填欄位',
        'must be a number': '必須為數字',
        'must be an integer': '必須為整數',
        'must be greater than': '必須大於',
        'must be less than': '必須小於',
        'invalid parameters': '參數無效',
        'invalid parameter': '參數無效'
    };

    // 尋找匹配的翻譯
    for (const [key, translation] of Object.entries(errorTranslations)) {
        if (errorMsg.includes(key)) {
            // 如果是完整匹配，直接返回翻譯
            if (errorMsg === key) {
                return translation;
            }
            // 如果是部分匹配，替換關鍵字
            return errorMsg.replace(key, translation);
        }
    }

    // 如果沒有匹配，返回原訊息
    return errorMsg;
};

// 取得欄位中文名稱
const getFieldLabel = (field) => {
    const fieldLabels = {
        'level.level': '等級',
        level: '等級',
        'unlock_description': '解鎖說明',
        identities: '身份限制',
        'rank_ids': '聘階限制',
        requirements: '升級條件',
        rewards: '升級獎勵'
    };
    return fieldLabels[field] || field;
};

// 生命周期
onMounted(() => {
    loadLevels();
    loadOptions();
    // 根據視圖模式載入資料
    if (viewMode.value === 'card') {
        loadCardViewLevels(false);
    } else if (viewMode.value === 'group') {
        loadGroupViewLevels(false);
    }
});

// 監聽視圖模式變化
watch(viewMode, (newMode) => {
    if (newMode === 'card') {
        loadCardViewLevels(false);
    } else if (newMode === 'group') {
        loadGroupViewLevels(false);
    }
});

// 監聽排序變化（需要重新載入）
watch([currentSort, currentOrder], () => {
    if (viewMode.value === 'card') {
        loadCardViewLevels(false);
    } else if (viewMode.value === 'group') {
        loadGroupViewLevels(false);
    }
});
</script>

<style scoped>
.page {
    padding: 24px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    min-height: 100vh;
}

.page-header {
    margin-bottom: 24px;
}

.page-header h1 {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 8px 0;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.page-header p {
    margin: 0;
    color: #6a6a7a;
    font-size: 14px;
    font-family: 'Courier New', monospace;
}

.content {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border: 2px solid transparent;
    border-radius: 16px;
    padding: 32px;
    min-height: 400px;
    position: relative;
    box-shadow: 0 0 30px rgba(42, 74, 122, 0.3);
}

.content::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude; /* Firefox 需用標準值，xor 僅 WebKit */
    -webkit-mask-composite: xor;
}

.content > * {
    position: relative;
    z-index: 1;
}

/* 工具欄 */
.toolbar {
    position: relative;
    z-index: 1;
}

.levels-toolbar-actions {
    border-top: 1px solid rgba(200, 200, 210, 0.35);
    padding-top: 16px;
}

.levels-sort-select {
    min-width: 120px;
}

.sort-controls {
    display: flex;
    align-items: center;
}

.sort-controls :deep(.ant-btn-group) {
    margin-left: 8px;
}

.sort-controls :deep(.ant-btn-group .ant-btn) {
    margin-left: 0;
}

.sort-controls :deep(.ant-btn-group .ant-btn + .ant-btn) {
    margin-left: 4px;
}

/* 排序按鈕樣式 */
.sort-order-btn,
.sort-order-btn.ant-btn,
.sort-order-btn.ant-btn-default {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
    border-radius: 8px !important;
    transition: all 0.3s ease !important;
    box-shadow: none !important;
}

.sort-order-btn:hover,
.sort-order-btn.ant-btn:hover,
.sort-order-btn.ant-btn-default:hover {
    background: rgba(42, 74, 122, 0.1) !important;
    border-color: rgba(42, 74, 122, 0.6) !important;
    color: #2a4a7a !important;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.2) !important;
}

.sort-order-active,
.sort-order-active.ant-btn,
.sort-order-active.ant-btn-default {
    background: rgba(42, 74, 122, 0.15) !important;
    border-color: rgba(42, 74, 122, 0.8) !important;
    color: #2a4a7a !important;
    font-weight: 600 !important;
}

.sort-order-active:hover,
.sort-order-active.ant-btn:hover,
.sort-order-active.ant-btn-default:hover {
    background: rgba(42, 74, 122, 0.25) !important;
    border-color: rgba(42, 74, 122, 0.9) !important;
    color: #1a4268 !important;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.3) !important;
}

.view-controls {
    display: flex;
    align-items: center;
}

.search-box {
    flex: 1;
    max-width: 400px;
}

/* 等級徽章 */
.level-badge {
    display: flex;
    align-items: center;
    gap: 8px;
}

.level-icon {
    color: #ffd700;
    font-size: 16px;
}

.level-text {
    color: #2a2a3a;
    font-weight: 600;
    font-family: 'Courier New', monospace;
}

/* 經驗值顯示 */
.exp-value {
    color: #2a2a3a;
    font-weight: 500;
    font-family: 'Courier New', monospace;
}

/* 描述文字 */
.description-text {
    color: #2a2a3a;
    max-width: 250px;
    word-break: break-word;
    line-height: 1.4;
}

/* 日期文字 */
.date-text {
    color: #6a6a7a;
    font-size: 13px;
    font-family: 'Courier New', monospace;
}

/* 等級詳情 */
.level-detail {
    color: #2a2a3a;
}

.detail-item {
    display: flex;
    align-items: flex-start;
    margin-bottom: 16px;
    gap: 12px;
}

.detail-item label {
    min-width: 100px;
    color: #6a6a7a;
    font-weight: 500;
    font-family: 'Courier New', monospace;
}

.detail-item .level-badge,
.detail-item .exp-value,
.detail-item .description-text,
.detail-item .date-text {
    flex: 1;
}

/* 詳情 Modal 樣式 */
.level-detail {
    color: #2a2a3a;
}

.level-descriptions {
    margin-top: 16px;
}

.tags-container {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.no-data {
    color: #999;
    font-style: italic;
    font-family: 'Courier New', monospace;
}

.requirements-list,
.point-requirements-list,
.rewards-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.requirement-item,
.point-requirement-item,
.reward-item {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.count-text,
.amount-text,
.quantity-text {
    color: #6a6a7a;
    font-size: 13px;
    font-family: 'Courier New', monospace;
}

.note-text {
    color: #999;
    font-size: 12px;
    font-style: italic;
    font-family: 'Courier New', monospace;
}

.summary-info {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    align-items: center;
}

/* 等級設定表單容器 */
.level-config-form-container {
    color: #2a2a3a;
    padding: 8px 0;
}

/* Collapse 摺疊面板樣式 */
.form-sections-collapse {
    background: transparent !important;
    border: none !important;
}

.form-sections-collapse :deep(.ant-collapse-item) {
    background: transparent !important;
    border: none !important;
    margin-bottom: 8px !important;
}

.form-sections-collapse :deep(.ant-collapse-header) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1px solid rgba(42, 74, 122, 0.2) !important;
    border-radius: 12px !important;
    padding: 8px 12px !important;
    transition: all 0.3s ease !important;
}

.form-sections-collapse :deep(.ant-collapse-header:hover) {
    background: rgba(255, 255, 255, 1) !important;
    border-color: rgba(42, 74, 122, 0.4) !important;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.2) !important;
}

.form-sections-collapse :deep(.ant-collapse-header:focus) {
    outline: none !important;
}

.form-sections-collapse :deep(.ant-collapse-content) {
    background: transparent !important;
    border: none !important;
    border-radius: 0 0 12px 12px !important;
    padding: 0 !important;
}

.form-sections-collapse :deep(.ant-collapse-content-box) {
    padding: 0 !important;
}

.form-sections-collapse :deep(.ant-collapse-expand-icon) {
    color: #2a4a7a !important;
    font-size: 14px !important;
}

.form-sections-collapse :deep(.ant-collapse-expand-icon:hover) {
    color: #3b6ea5 !important;
}

.form-section-panel {
    border: none !important;
}

.form-section-card-inner {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1px solid rgba(42, 74, 122, 0.2) !important;
    border-radius: 8px !important;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.1) !important;
    margin-top: 4px !important;
}

.form-section-card-inner :deep(.ant-card-body) {
    padding: 12px !important;
}

.section-header {
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    font-size: 15px !important;
}

/* 區塊樣式 */
.limits-section,
.requirements-section,
.point-requirements-section,
.rewards-section {
    padding: 8px 0;
}

.section-item {
    margin-bottom: 16px;
}

.section-item:last-child {
    margin-bottom: 0;
}

.section-divider {
    margin: 16px 0 !important;
    border-color: rgba(42, 74, 122, 0.2) !important;
}

.rank-limits-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.rank-limit-item {
    background: rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(42, 74, 122, 0.2);
    border-radius: 8px;
    padding: 12px;
    transition: all 0.3s ease;
}

.rank-limit-item:hover {
    border-color: rgba(42, 74, 122, 0.4);
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.15);
}

.section-title {
    margin: 0 0 8px 0;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-weight: 600;
    font-size: 16px;
}

.section-desc {
    margin: 0 0 8px 0;
    color: #6a6a7a;
    font-size: 13px;
    font-family: 'Courier New', monospace;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
}

.section-header .section-title {
    margin-bottom: 4px;
}

.empty-state {
    text-align: center;
    color: #999;
    padding: 20px 0;
    font-family: 'Courier New', monospace;
    font-style: italic;
}

/* 列表項目樣式 */
.requirements-list,
.point-requirements-list,
.rewards-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.requirement-item,
.point-requirement-item,
.reward-item,
.identity-limit-item {
    background: rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(42, 74, 122, 0.2);
    border-radius: 8px;
    padding: 12px;
    transition: all 0.3s ease;
}

.requirement-item:hover,
.point-requirement-item:hover,
.reward-item:hover,
.identity-limit-item:hover {
    border-color: rgba(42, 74, 122, 0.4);
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.15);
}

.identity-limits-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.placeholder-text {
    color: #999;
    font-style: italic;
    font-family: 'Courier New', monospace;
    font-size: 13px;
}

/* 表單樣式增強 */
.vaporwave-form :deep(.ant-form-item-label > label) {
    color: #2a4a7a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
}

.vaporwave-form :deep(.ant-input),
.vaporwave-form :deep(.ant-input-number),
.vaporwave-form :deep(.ant-input-number-input),
.vaporwave-form :deep(.ant-select .ant-select-selector),
.vaporwave-form :deep(.ant-textarea) {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 1px solid rgba(42, 74, 122, 0.3) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
}

.vaporwave-form :deep(.ant-input:focus),
.vaporwave-form :deep(.ant-input-number:focus),
.vaporwave-form :deep(.ant-input-number-focused),
.vaporwave-form :deep(.ant-select:focus .ant-select-selector),
.vaporwave-form :deep(.ant-select-focused .ant-select-selector),
.vaporwave-form :deep(.ant-textarea:focus) {
    border-color: #2a4a7a !important;
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.2) !important;
}

.vaporwave-form :deep(.ant-input::placeholder),
.vaporwave-form :deep(.ant-textarea::placeholder) {
    color: rgba(106, 106, 122, 0.6) !important;
}

.w-full {
    width: 100%;
}

/* 視圖切換 */
.view-mode-switch {
    margin-right: 16px;
}

.view-mode-switch :deep(.ant-radio-button-wrapper) {
    font-family: 'Courier New', monospace !important;
    border-color: rgba(42, 74, 122, 0.3) !important;
    color: #2a2a3a !important;
}

.view-mode-switch :deep(.ant-radio-button-wrapper-checked) {
    background: linear-gradient(45deg, #2a4a7a, #3b6ea5) !important;
    border-color: #2a4a7a !important;
    color: #fff !important;
}

/* 等級範圍摘要 - 緊湊版 */
.level-summary-compact {
    background: rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(42, 74, 122, 0.2);
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 16px;
}

.summary-row {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.summary-label {
    color: #6a6a7a;
    font-family: 'Courier New', monospace;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
}

.summary-items {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
}

.summary-item-tag {
    margin: 0 !important;
    font-family: 'Courier New', monospace;
    font-size: 12px;
}

/* 卡片視圖 */
.levels-card-view {
    margin-top: 16px;
}

/* 載入更多按鈕 */
.load-more-wrapper {
    display: flex;
    justify-content: center;
    margin-top: 24px;
    padding: 16px 0;
    width: 100%;
    grid-column: 1 / -1;
    /* 在 grid 佈局中跨越所有列 */
}

.levels-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 12px;
}

.level-card {
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(42, 74, 122, 0.3);
    border-radius: 12px;
    padding: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
}

.level-card-actions {
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    gap: 4px;
    z-index: 10;
    opacity: 0;
    transition: opacity 0.2s ease;
}

.level-card:hover .level-card-actions {
    opacity: 1;
}

.level-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
        135deg,
        rgba(255, 158, 207, 0.05),
        rgba(42, 74, 122, 0.05),
        rgba(138, 175, 255, 0.05)
    );
    opacity: 0;
    transition: opacity 0.3s ease;
}

.level-card:hover {
    transform: translateY(-5px);
    border-color: #2a4a7a;
    box-shadow:
        0 8px 25px rgba(42, 74, 122, 0.25),
        0 4px 20px rgba(168, 255, 246, 0.15);
}

.level-card:hover::before {
    opacity: 1;
}

.level-card-header {
    margin-bottom: 12px;
    position: relative;
    z-index: 1;
}

.level-badge-large {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 10px;
    background: linear-gradient(
        45deg,
        rgba(42, 74, 122, 0.1),
        rgba(138, 175, 255, 0.1)
    );
    border-radius: 8px;
}

.level-text-large {
    color: #2a2a3a;
    font-weight: 700;
    font-family: 'Courier New', monospace;
    font-size: 14px;
    text-shadow: 0 0 8px rgba(42, 74, 122, 0.2);
}

.level-card-body {
    margin-bottom: 10px;
    position: relative;
    z-index: 1;
}

.level-description {
    color: #6a6a7a;
    font-family: 'Courier New', monospace;
    font-size: 11px;
    line-height: 1.4;
    margin-bottom: 8px;
    min-height: 32px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.level-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.level-card-footer {
    border-top: 1px solid rgba(42, 74, 122, 0.15);
    padding-top: 8px;
    margin-top: 6px;
    position: relative;
    z-index: 1;
    display: flex;
    justify-content: flex-end;
}

.empty-levels {
    text-align: center;
    padding: 60px 20px;
    color: #999;
    font-family: 'Courier New', monospace;
    font-style: italic;
}

/* DataTable component integration - 與會員列表統一 */
:deep(.ant-card) {
    background: transparent !important;
    border: none !important;
}

:deep(.ant-card-body) {
    padding: 0 !important;
}

/* 表單樣式 */
.vaporwave-form :deep(.ant-form-item-label > label) {
    color: #2a4a7a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
}

.vaporwave-form :deep(.ant-input),
.vaporwave-form :deep(.ant-input-number),
.vaporwave-form :deep(.ant-input-number-input) {
    background: rgba(26, 26, 46, 0.8);
    border: 1px solid rgba(42, 74, 122, 0.3);
    color: #2a2a3a;
}

.vaporwave-form :deep(.ant-input:focus),
.vaporwave-form :deep(.ant-input-number:focus),
.vaporwave-form :deep(.ant-input-number-focused) {
    border-color: #2a4a7a;
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.2);
}

.vaporwave-form :deep(.ant-input::placeholder) {
    color: rgba(168, 255, 246, 0.8);
}

/* 刪除按鈕樣式 */
.delete-btn {
    padding: 0 !important;
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
    min-width: auto !important;
    height: auto !important;
}

.delete-btn :deep(.ant-btn) {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
}

.delete-btn :deep(.anticon) {
    color: #ff4d4f !important;
    font-size: 16px;
    transition: color 0.3s ease;
}

.delete-btn:hover :deep(.anticon) {
    color: #ff7875 !important;
}

.delete-btn:hover {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
}

.delete-btn:focus {
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
}

.delete-btn:active {
    border: none !important;
    box-shadow: none !important;
}

/* 確保 select 寬度正確 */
.w-full {
    width: 100% !important;
}

/* 等級輸入框寬度限制 */
.level-input {
    width: 120px !important;
}

/* 按鈕樣式 - 與會員列表統一 */
:deep(.ant-btn) {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(168, 255, 246, 0.5) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    border-radius: 8px !important;
}

:deep(.ant-btn:hover) {
    background: rgba(168, 255, 246, 0.1) !important;
    border-color: #0e7490 !important;
    color: #2a2a3a !important;
}

:deep(.ant-btn-primary) {
    background: linear-gradient(45deg, #2a4a7a, #3b6ea5) !important;
    border: none !important;
    color: #2a2a3a !important;
    font-weight: 500 !important;
}

:deep(.ant-btn-primary:hover) {
    background: linear-gradient(45deg, #3b6ea5, #2a4a7a) !important;
    box-shadow: 0 0 10px rgba(42, 74, 122, 0.4) !important;
}

:deep(.ant-btn-link) {
    color: #3b6ea5 !important;
}

:deep(.ant-btn-link:hover) {
    color: #0e7490 !important;
}

:deep(.ant-btn-link.ant-btn-dangerous) {
    color: #ff6b6b !important;
}

:deep(.ant-btn-link.ant-btn-dangerous:hover) {
    color: #ff5252 !important;
}

:deep(.ant-btn:not([class*='action-btn-']):not(.delete-btn) .anticon) {
    color: #2a2a3a !important;
}

:deep(.ant-btn:not([class*='action-btn-']):not(.delete-btn):hover .anticon) {
    color: #64748b !important;
}

/* 模態框樣式 */
:deep(.ant-modal-content) {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border: 2px solid rgba(42, 74, 122, 0.3);
}

:deep(.ant-modal-header) {
    background: transparent;
    border-bottom: 1px solid rgba(42, 74, 122, 0.3);
}

:deep(.ant-modal-title) {
    color: #0e7490;
    font-family: 'Courier New', monospace;
}

:deep(.ant-modal-body) {
    color: #2a2a3a;
}

/* 分組視圖樣式（圖表形式） */
.levels-group-view {
    margin-top: 24px;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 16px;
    align-items: start;
}

.group-section {
    min-width: 0;
    /* 允許 grid item 縮小 */
}

.group-header-card {
    background: linear-gradient(
        135deg,
        rgba(42, 74, 122, 0.1),
        rgba(138, 175, 255, 0.1)
    );
    border: 1px solid rgba(42, 74, 122, 0.3);
    border-radius: 12px;
    padding: 10px 12px;
    margin-bottom: 10px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.group-header-card:hover {
    background: linear-gradient(
        135deg,
        rgba(42, 74, 122, 0.15),
        rgba(138, 175, 255, 0.15)
    );
    border-color: #0e7490;
}

.group-title-row {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.group-expand-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    color: #3b6ea5;
    font-size: 14px;
    transition: all 0.2s ease;
}

.group-header-card:hover .group-expand-icon {
    color: #0e7490;
}

.title-icon {
    font-size: 24px;
}

.group-title {
    font-size: 16px;
    font-weight: 600;
    color: #2a2a3a;
    margin: 0;
    font-family: 'Courier New', monospace;
}

.level-range-badge {
    background: rgba(42, 74, 122, 0.2);
    color: #2a4a7a;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    font-family: 'Courier New', monospace;
}

.levels-timeline {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.level-row-card {
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(42, 74, 122, 0.3);
    border-radius: 12px;
    padding: 0;
    transition: all 0.3s ease;
    overflow: hidden;
}

.level-row-card:hover {
    border-color: #0e7490;
    box-shadow: 0 4px 16px rgba(42, 74, 122, 0.2);
}

.level-row-card.expanded {
    border-color: #0e7490;
    box-shadow: 0 4px 16px rgba(42, 74, 122, 0.3);
}

.level-row-header {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 12px;
    cursor: pointer;
    transition: background 0.2s ease;
}

.level-row-header:hover {
    background: rgba(42, 74, 122, 0.05);
}

.level-row-expanded {
    padding: 0 12px 12px 12px;
    border-top: 1px solid rgba(42, 74, 122, 0.15);
    margin-top: 8px;
    padding-top: 12px;
    animation: slideDown 0.3s ease;
}

@keyframes slideDown {
    from {
        opacity: 0;
        max-height: 0;
    }

    to {
        opacity: 1;
        max-height: 500px;
    }
}

.level-info-summary {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
}

.level-number {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 70px;
    flex-shrink: 0;
}

.trophy-icon {
    font-size: 20px;
    color: #ffa87a;
}

.level-text {
    font-size: 14px;
    font-weight: 600;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.level-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
    /* 允許 flex item 縮小 */
}

.info-row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    flex-wrap: wrap;
}

.description-row {
    margin-bottom: 4px;
}

.description-text {
    font-size: 11px;
    line-height: 1.4;
    color: #6a6a7a;
    font-family: 'Courier New', monospace;
}

.info-label {
    font-size: 12px;
    font-weight: 600;
    color: #666;
    min-width: 65px;
    flex-shrink: 0;
    font-family: 'Courier New', monospace;
}

.info-value {
    font-size: 12px;
    color: #2a2a3a;
    flex: 1;
    min-width: 0;
    /* 允許文字換行 */
    font-family: 'Courier New', monospace;
}

.tags-container {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    flex: 1;
}

.no-data-text {
    font-size: 14px;
    color: #999;
    font-style: italic;
    font-family: 'Courier New', monospace;
}

.level-actions {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
}

.action-btn {
    width: 32px;
    height: 32px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    transition: all 0.2s ease;
}

.expand-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    cursor: pointer;
    color: #3b6ea5;
    transition: all 0.2s ease;
    border-radius: 4px;
}

.expand-icon:hover {
    background: rgba(138, 175, 255, 0.1);
    color: #0e7490;
}

.more-indicator {
    font-size: 11px;
    color: #999;
    font-family: 'Courier New', monospace;
    margin-left: 4px;
}

.empty-state {
    text-align: center;
    padding: 48px;
    color: #999;
    font-family: 'Courier New', monospace;
}

/* 標籤統一樣式 */
.level-tags :deep(.ant-tag) {
    border-radius: 8px;
    font-size: 11px;
    font-weight: 600;
    font-family: 'Courier New', monospace;
    padding: 3px 8px;
    border: none;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    margin-right: 4px;
    margin-bottom: 4px;
}

.level-tags :deep(.ant-tag-blue) {
    background: linear-gradient(135deg, #3b6ea5, #0e7490);
    color: #fff;
}

.level-tags :deep(.ant-tag-purple) {
    background: linear-gradient(135deg, #2a4a7a, #64748b);
    color: #fff;
}

/* 條件和獎勵標籤樣式 */
.requirement-badge {
    display: inline-flex;
    align-items: center;
    padding: 3px 8px;
    border-radius: 8px;
    font-size: 11px;
    font-weight: 600;
    font-family: 'Courier New', monospace;
    background: linear-gradient(135deg, #ffa87a, #64748b);
    color: #fff;
    border: none;
    box-shadow: 0 1px 3px rgba(255, 168, 122, 0.3);
    margin-right: 4px;
    margin-bottom: 4px;
}

.reward-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 3px 8px;
    border-radius: 8px;
    font-size: 11px;
    font-weight: 600;
    font-family: 'Courier New', monospace;
    background: linear-gradient(135deg, #d0ffb7, #0e7490);
    color: #2a2a3a;
    border: none;
    box-shadow: 0 1px 3px rgba(168, 255, 246, 0.3);
    margin-right: 4px;
    margin-bottom: 4px;
}

/* 分頁組件樣式 */
.pagination-wrapper {
    margin-top: 24px;
    display: flex;
    justify-content: center;
    padding: 16px 0;
}

.pagination-wrapper :deep(.ant-pagination) {
    font-family: 'Courier New', monospace;
}

.pagination-wrapper :deep(.ant-pagination-item) {
    border-color: rgba(42, 74, 122, 0.3);
    background: rgba(255, 255, 255, 0.9);
}

.pagination-wrapper :deep(.ant-pagination-item:hover) {
    border-color: #0e7490;
}

.pagination-wrapper :deep(.ant-pagination-item-active) {
    background: linear-gradient(45deg, #2a4a7a, #3b6ea5);
    border-color: #0e7490;
}

.pagination-wrapper :deep(.ant-pagination-item-active a) {
    color: #fff;
}

.pagination-wrapper :deep(.ant-pagination-prev),
.pagination-wrapper :deep(.ant-pagination-next) {
    border-color: rgba(42, 74, 122, 0.3);
    background: rgba(255, 255, 255, 0.9);
}

.pagination-wrapper :deep(.ant-pagination-prev:hover),
.pagination-wrapper :deep(.ant-pagination-next:hover) {
    border-color: #0e7490;
}

.pagination-wrapper :deep(.ant-pagination-options) {
    font-family: 'Courier New', monospace;
}

/* Popconfirm 樣式 */
:deep(.ant-popover-content) {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border: 1px solid rgba(42, 74, 122, 0.3);
}

:deep(.ant-popover-inner) {
    background: transparent;
    color: #2a2a3a;
}

:deep(.ant-popover-message-title) {
    color: #2a2a3a;
}

/* 統一篩選欄位樣式 */
.filters-grid :deep(.status-select),
.toolbar :deep(.status-select) {
    height: 32px;
    font-size: 13px;
    border-radius: 8px;
    border: 1.5px solid rgba(200, 200, 210, 0.4);
    background: rgba(255, 255, 255, 0.95);
    color: #2a2a3a;
    transition: all 0.3s ease;
}

.filters-grid :deep(.status-select:hover),
.toolbar :deep(.status-select:hover) {
    border-color: rgba(42, 74, 122, 0.6);
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.2);
}

.filters-grid :deep(.status-select:focus),
.filters-grid :deep(.status-select.ant-select-focused),
.toolbar :deep(.status-select:focus),
.toolbar :deep(.status-select.ant-select-focused) {
    border-color: rgba(42, 74, 122, 0.8);
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.2);
}

/* 輸入數字框樣式 */
.filters-grid :deep(.ant-input-number.status-select) {
    height: 32px;
}

/* 刪除確認 Modal 樣式 */
.delete-confirm-content {
    padding: 8px 0;
}

.delete-warning-alert {
    margin-bottom: 20px;
    border: 1px solid rgba(255, 167, 122, 0.5);
    background: rgba(255, 167, 122, 0.1);
    border-radius: 8px;
}

.delete-warning-alert :deep(.ant-alert-message) {
    color: #ffa87a;
    font-family: 'Courier New', monospace;
    font-weight: 600;
}

.warning-text {
    color: #ffa87a;
    font-family: 'Courier New', monospace;
}

.level-info-preview {
    margin-top: 16px;
}

.level-preview-descriptions {
    background: rgba(255, 255, 255, 0.9);
    border-radius: 8px;
}

.level-preview-descriptions :deep(.ant-descriptions-item-label) {
    color: #2a4a7a;
    font-family: 'Courier New', monospace;
    font-weight: 600;
    background: rgba(42, 74, 122, 0.1);
}

.level-preview-descriptions :deep(.ant-descriptions-item-content) {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.level-badge-small {
    display: inline-flex;
    align-items: center;
    padding: 4px 8px;
    background: linear-gradient(
        45deg,
        rgba(42, 74, 122, 0.1),
        rgba(138, 175, 255, 0.1)
    );
    border-radius: 6px;
}

.level-text-small {
    color: #2a2a3a;
    font-weight: 700;
    font-family: 'Courier New', monospace;
    font-size: 13px;
}

.description-text-small {
    color: #6a6a7a;
    font-family: 'Courier New', monospace;
    font-size: 12px;
}

.tags-container-small {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.no-data-text-small {
    color: #999;
    font-family: 'Courier New', monospace;
    font-style: italic;
    font-size: 12px;
}

.filters-grid :deep(.ant-input-number.status-select .ant-input-number-input) {
    height: 30px;
    font-size: 13px;
    padding: 0 12px;
    border-radius: 8px;
}

/* 篩選操作按鈕容器 */
.filter-actions {
    display: flex;
    gap: 8px;
}

/* 確保查詢和重置按鈕樣式優先 */
.filter-actions .unified-search-btn,
.filter-actions .unified-search-btn.ant-btn,
.filter-actions .unified-search-btn.ant-btn-default,
.filter-actions :deep(.unified-search-btn),
.filter-actions :deep(.unified-search-btn.ant-btn),
.filter-actions :deep(.unified-search-btn.ant-btn-default) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid #7a9ba5 !important;
    color: #7a9ba5 !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
    border-radius: 8px !important;
    transition: all 0.3s ease !important;
    box-shadow: none !important;
}

.filter-actions .unified-search-btn:hover,
.filter-actions .unified-search-btn.ant-btn:hover,
.filter-actions .unified-search-btn.ant-btn-default:hover,
.filter-actions :deep(.unified-search-btn:hover),
.filter-actions :deep(.unified-search-btn.ant-btn:hover),
.filter-actions :deep(.unified-search-btn.ant-btn-default:hover) {
    background: rgba(122, 155, 165, 0.1) !important;
    border-color: #7a9ba5 !important;
    color: #7a9ba5 !important;
    box-shadow: 0 2px 8px rgba(122, 155, 165, 0.3) !important;
}

.filter-actions .unified-reset-btn,
.filter-actions .unified-reset-btn.ant-btn,
.filter-actions .unified-reset-btn.ant-btn-default,
.filter-actions :deep(.unified-reset-btn),
.filter-actions :deep(.unified-reset-btn.ant-btn),
.filter-actions :deep(.unified-reset-btn.ant-btn-default) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid #c4a5a5 !important;
    color: #c4a5a5 !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
    border-radius: 8px !important;
    transition: all 0.3s ease !important;
    box-shadow: none !important;
}

.filter-actions .unified-reset-btn:hover,
.filter-actions .unified-reset-btn.ant-btn:hover,
.filter-actions .unified-reset-btn.ant-btn-default:hover,
.filter-actions :deep(.unified-reset-btn:hover),
.filter-actions :deep(.unified-reset-btn.ant-btn:hover),
.filter-actions :deep(.unified-reset-btn.ant-btn-default:hover) {
    background: rgba(196, 165, 165, 0.1) !important;
    border-color: #c4a5a5 !important;
    color: #c4a5a5 !important;
    box-shadow: 0 2px 8px rgba(196, 165, 165, 0.3) !important;
}
</style>
